import { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { resourceEndpoints } from '../../services/api/endpoints/resource.endpoint';
import { workingHoursEndpoints } from '../../services/api/endpoints/workingHours.endpoint';
import { CreateResourceDTO, UpdateResourceDTO } from '../../types/resource';
import { logger } from '../../utils/logger';
import { serviceMappingEndpoints } from '../../services/api/endpoints/serviceMapping.endpoint';
import { convertTo12HourFormat } from '../../utils/timeUtils';

export const useResourceActions = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedOrganizationId = useSelector(
    (state: RootState) => state.organizationContext.selectedOrganizationId
  );

  const createResource = async (data: CreateResourceDTO) => {
    if (!selectedOrganizationId) {
      throw new Error('No organization selected');
    }

    setIsLoading(true);
    setError(null);

    const logAndSetError = (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Failed to create resource';
      setError(message);
      logger.error('Error creating resource:', err);
      throw err;
    };

    try {
      const { useOrganizationHours, workingHours, services, ...resourceData } = data;
      const resource = await resourceEndpoints.createResource(selectedOrganizationId, resourceData);

      if (resource.id) {
        if (services?.length) {
          logger.info('Creating services for resource:', resource.id);

          const serviceMappingPromises = services.map(service => {
            const { serviceId } = service;
            return serviceMappingEndpoints.createMapping(
              selectedOrganizationId,
              serviceId,
              resource.id
            );
          });

          const serviceResults = await Promise.allSettled(serviceMappingPromises);
          const failedServices = serviceResults.filter(result => result.status === 'rejected');

          if (failedServices.length) {
            logger.error('Error creating some service mappings:', failedServices);
          } else {
            logger.info('All service mappings created successfully.');
          }
        }
        if (workingHours?.length) {
          logger.info('Creating working hours for resource:', workingHours);

          const createWorkingHours = workingHours.filter(wh => wh.enabled);

          const formattedWorkingHours = createWorkingHours.map(wh => ({
            day: wh.day.toLowerCase(),
            startTime: convertTo12HourFormat(wh.startTime),
            endTime: convertTo12HourFormat(wh.endTime),
            resourceId: resource.id,
            orgId: selectedOrganizationId,
          }));

          await workingHoursEndpoints.createMultipleWorkingHours(
            selectedOrganizationId,
            formattedWorkingHours
          );
        }
      }

      return resource;
    } catch (err) {
      logAndSetError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const updateResource = async (resourceId: number, data: UpdateResourceDTO) => {
    if (!selectedOrganizationId) {
      throw new Error('No organization selected');
    }

    setIsLoading(true);
    setError(null);

    try {
      const { useOrganizationHours, workingHours, services, ...resourceData } = data;

      const updatedResource = await resourceEndpoints.updateResource(
        selectedOrganizationId,
        resourceId,
        resourceData
      );

      if (services?.length) {
        logger.info('Updating services for resource:', resourceId);

        const serviceMappingPromises = services.map(service => {
          const { serviceId, isSelected, isRemoved, isModified, mappingId } = service;

          // Condition for deleting the mapping
          if (!isSelected && isRemoved && isModified) {
            return serviceMappingEndpoints.deleteMapping(selectedOrganizationId, mappingId);
          }

          // Condition for creating the mapping
          if (isSelected && !isRemoved && isModified) {
            return serviceMappingEndpoints.createMapping(
              selectedOrganizationId,
              serviceId,
              resourceId
            );
          }

          return Promise.resolve();
        });

        const serviceResults = await Promise.allSettled(serviceMappingPromises);
        const failedServices = serviceResults.filter(result => result.status === 'rejected');

        if (failedServices.length) {
          logger.error('Error creating some service mappings:', failedServices);
        } else {
          logger.info('All service mappings created successfully.');
        }
      }

      if (workingHours?.length) {
        logger.info('Updating working hours for resource:', resourceId);
        const deleteWorkingHours = workingHours.filter(wh => wh.id && !wh.enabled);
        const updateWorkingHours = workingHours.filter(wh => wh.id && wh.enabled && wh.isModified);
        const createWorkingHours = workingHours.filter(wh => !wh.id && wh.enabled);

        if (createWorkingHours.length) {
          const formattedWorkingHours = createWorkingHours.map(wh => ({
            day: wh.day.toLowerCase(),
            startTime: convertTo12HourFormat(wh.startTime),
            endTime: convertTo12HourFormat(wh.endTime),
            resourceId,
            orgId: selectedOrganizationId,
          }));

          await workingHoursEndpoints.createMultipleWorkingHours(
            selectedOrganizationId,
            formattedWorkingHours
          );
        }

        if (updateWorkingHours.length) {
          const formattedWorkingHours = updateWorkingHours.map(wh => ({
            id: wh.id,
            day: wh.day.toLowerCase(),
            startTime: convertTo12HourFormat(wh.startTime),
            endTime: convertTo12HourFormat(wh.endTime),
            resourceId,
            orgId: selectedOrganizationId,
          }));

          await workingHoursEndpoints.updateWorkingHours(
            selectedOrganizationId,
            formattedWorkingHours
          );
        }

        if (deleteWorkingHours.length) {
          logger.info('Deleting working hours:', deleteWorkingHours);
          await workingHoursEndpoints.deleteAllWorkingHours(
            selectedOrganizationId,
            deleteWorkingHours
          );
        }
      }
      return updatedResource;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update resource';
      setError(message);
      logger.error('Error updating resource:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteResource = async (resourceId: number) => {
    if (!selectedOrganizationId) {
      throw new Error('No organization selected');
    }

    setIsLoading(true);
    setError(null);

    try {
      await resourceEndpoints.deleteResource(selectedOrganizationId, resourceId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete resource';
      setError(message);
      logger.error('Error deleting resource:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    createResource,
    updateResource,
    deleteResource,
    isLoading,
    error,
  };
};
